import { useQuery } from '@tanstack/react-query';
import { usersApi, type PprJob } from '@/lib/apiClient';
import { fmtInstantDate } from '@/lib/dates';

/**
 * The read-only cancellation record: why a review was stopped, when, and by whom
 * — plus the reopening, if it came back.
 *
 * Separate from CancelReviewActions, and rendered for EVERY viewer, because of
 * two regressions the move out of `notes` introduced (#344):
 *
 *  1. The reason used to live in notes, which the Notes & History tab shows to
 *     anyone who can open the review. Once it moved to its own column, the only
 *     thing rendering it was a component gated on `canEdit` — so an Accounting
 *     user with `ppr.view` saw a Cancelled badge with no explanation at all.
 *  2. cancelled_* is deliberately KEPT after a reopen, but the old banner only
 *     rendered while status was still Cancelled. The retained history was
 *     therefore unreachable the moment it became historical, which made
 *     "preserve the record" hollow.
 *
 * So this renders on the presence of `cancelledAt`, not on the current status.
 */
export default function CancellationRecord({ job }: { job: PprJob }) {
  // Same cached list NotesHistoryTab uses to resolve audit user ids (5 min,
  // usually already in cache). An audit line reading "by 3f2a-…" helps nobody.
  const { data: users } = useQuery({
    queryKey: ['users', 'list'],
    queryFn: () => usersApi.list(),
    staleTime: 5 * 60 * 1000,
  });
  const nameOf = (uid: string | null | undefined): string | null => {
    if (!uid) return null;
    const u = users?.find((x) => x.id === uid);
    return u?.displayName || u?.email || null;
  };

  if (!job.cancelledAt) return null;

  const reopened = !!job.reopenedAt && job.status !== 'Cancelled';
  const by = nameOf(job.cancelledById);
  const reopenedBy = nameOf(job.reopenedById);

  return (
    <>
      <style>{css}</style>
      <div className={`pprcr ${reopened ? 'pprcr--past' : 'pprcr--current'}`}>
        <div className="pprcr-head">
          {reopened ? 'Previously cancelled' : 'Cancelled'}
          {/* fmtInstantDate, NOT fmtDate: cancelled_at is a DATETIME2 — a real
              moment — and fmtDate slices the date portion off the string and
              rebuilds it locally, which renders an 8pm-Eastern timestamp as
              tomorrow. See the warning in lib/dates.ts. */}
          {` ${fmtInstantDate(job.cancelledAt)}`}
          {by ? ` by ${by}` : ''}
        </div>
        {job.cancelledReason && (
          <div className="pprcr-reason">{job.cancelledReason}</div>
        )}
        {reopened && (
          <div className="pprcr-foot">
            Reopened {fmtInstantDate(job.reopenedAt)}
            {reopenedBy ? ` by ${reopenedBy}` : ''}.
          </div>
        )}
      </div>
    </>
  );
}

// pprcr- prefix: module-local, as with the other PPR components.
const css = `
  .pprcr {
    padding: 10px 14px;
    margin-bottom: var(--ew-s-4);
    border-radius: var(--ew-r-md);
    font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1.5 var(--ew-font);
  }
  /* Still cancelled: the same muted red-grey as the status badge. */
  .pprcr--current {
    background: #F0EAE8;
    color: #7A5C55;
    border: 1px solid rgba(122, 92, 85, 0.3);
  }
  /* Historical: plain neutral, so a live review doesn't look alarming. */
  .pprcr--past {
    background: var(--ew-n-50);
    color: var(--ew-n-600);
    border: 1px solid var(--ew-n-200);
  }
  .pprcr-head { font-weight: 600; }
  .pprcr-reason {
    margin-top: 4px;
    white-space: pre-wrap;
  }
  .pprcr-foot {
    margin-top: 4px;
    font: var(--ew-fw-regular) var(--ew-fs-caption)/1.4 var(--ew-font);
  }
`;
